import { useTranslation } from 'react-i18next';
import { Loader2 } from 'lucide-react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { useMaterialsList } from '@/features/masters/materials/useMaterials';

import { useSubmitInventoryAdjustment } from './useInventory';
import { adjustmentProposalSchema, type AdjustmentProposalInput } from './types';

const NS = 'operations';

interface Props {
  batchId: string;
  open: boolean;
  onOpenChange: (v: boolean) => void;
}

/**
 * Field supervisor+ submits an inventory adjustment proposal. The proposal is
 * queued in `audit_approvals` (approval_type='inventory_adjustment') and the
 * actual `material_inventory_movements` row is only written on manager
 * approval.
 */
export const InventoryAdjustmentDialog = ({ batchId, open, onOpenChange }: Props) => {
  const { t } = useTranslation(NS);
  const submit = useSubmitInventoryAdjustment();
  const materialsQuery = useMaterialsList({ status: 'approved', per_page: 200 });
  const materials = materialsQuery.data?.rows ?? [];

  const form = useForm<AdjustmentProposalInput>({
    resolver: zodResolver(adjustmentProposalSchema),
    defaultValues: {
      material_id: '',
      kg_delta: '',
      reason: '',
      location: '',
    },
  });

  const onSubmit = form.handleSubmit(async (values) => {
    await submit.mutateAsync({
      batchId,
      body: {
        ...values,
        location: values.location?.trim() || undefined,
      },
    });
    form.reset();
    onOpenChange(false);
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t('efs.operations.inventory.adjustment.title')}</DialogTitle>
          <DialogDescription>
            {t('efs.operations.inventory.adjustment.proposal_hint')}
          </DialogDescription>
        </DialogHeader>
        <Form {...form}>
          <form onSubmit={onSubmit} className="space-y-4" noValidate>
            <FormField
              control={form.control}
              name="material_id"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('efs.operations.inventory.fields.material')}</FormLabel>
                  <FormControl>
                    <Select value={field.value} onValueChange={field.onChange}>
                      <SelectTrigger>
                        <SelectValue
                          placeholder={t(
                            'efs.operations.inventory.adjustment.material_placeholder',
                          )}
                        />
                      </SelectTrigger>
                      <SelectContent>
                        {materials.map((m) => (
                          <SelectItem key={m.material_id} value={m.material_id}>
                            {m.japanese_name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="kg_delta"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('efs.operations.inventory.fields.kg_delta')}</FormLabel>
                  <FormControl>
                    <Input type="text" inputMode="decimal" placeholder="0.000" {...field} />
                  </FormControl>
                  <FormDescription>
                    {t('efs.operations.inventory.adjustment.kg_delta_hint')}
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="reason"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('efs.operations.inventory.adjustment.reason')}</FormLabel>
                  <FormControl>
                    <Textarea rows={3} {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="location"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('efs.operations.inventory.fields.location')}</FormLabel>
                  <FormControl>
                    <Input type="text" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenChange(false)}
                disabled={submit.isPending}
              >
                {t('efs.common.actions.cancel')}
              </Button>
              <Button type="submit" disabled={submit.isPending}>
                {submit.isPending ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : null}
                {t('efs.operations.inventory.adjustment.submit')}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};

export default InventoryAdjustmentDialog;
